import {
  createPurchaseTracker, resolveAdsConfiguration,
  type AdsConsent, type PurchaseReceipt,
} from '@/lib/googleAdsTrackingCore';

const CONSENT_KEY = 'jampackd:ads-consent:v1';
const CLICK_KEY = 'jampackd:ads-click:v1';
export const ADS_CONSENT_EVENT = 'jampackd:ads-consent-changed';
export const ADS_PREFERENCES_EVENT = 'jampackd:ads-preferences';
const clickNames = ['gclid', 'gbraid', 'wbraid'] as const;
const consentDenied = { ad_storage: 'denied', analytics_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' };
type GoogleWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };
const googleWindow = window as GoogleWindow;
const config = resolveAdsConfiguration(import.meta.env, window.location.hostname);
let currentConsent: AdsConsent = 'unset';
let consentInMemoryOnly = false;
let tagPromise: Promise<boolean> | null = null;
let tagReady = false;
let pendingClickParameters = getAdsConsent() === 'denied' ? {} : readClickParameters(window.location.href);

function readClickParameters(href: string): Record<string, string> {
  const params = new URL(href).searchParams;
  return Object.fromEntries(clickNames.flatMap(name => {
    const value = params.get(name);
    return value && /^[a-zA-Z0-9_.~-]{1,1024}$/.test(value) ? [[name, value]] : [];
  }));
}

export function isGoogleAdsConfigured(): boolean { return Boolean(config); }

export function getAdsConsent(): AdsConsent {
  // Read the shared preference again before every emission, including across tabs.
  // If this visit cannot persist a choice, preserve that explicit choice in memory.
  if (consentInMemoryOnly) return currentConsent;
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    currentConsent = value === 'granted' || value === 'denied' ? value : 'unset';
  } catch { /* Retain this visit's choice when browser storage is unavailable. */ }
  return currentConsent;
}

function withdrawAdsConsent(): void {
  pendingClickParameters = {};
  try { sessionStorage.removeItem(CLICK_KEY); } catch { /* Optional storage. */ }
  // A previously loaded tag receives the withdrawal; new visits never load it.
  googleWindow.gtag?.('consent', 'update', consentDenied);
  for (const part of document.cookie.split(';')) {
    const name = part.trim().split('=')[0];
    if (!name.startsWith('_gcl_')) continue;
    for (const domain of ['', `; domain=${window.location.hostname}`, '; domain=.jampackd.store']) {
      document.cookie = `${name}=; Max-Age=0; path=/${domain}; SameSite=Lax`;
    }
  }
}

export function setAdsConsent(consent: 'granted' | 'denied'): void {
  currentConsent = consent;
  try {
    localStorage.setItem(CONSENT_KEY, consent);
    consentInMemoryOnly = false;
  } catch { consentInMemoryOnly = true; }
  if (consent === 'denied') withdrawAdsConsent();
  window.dispatchEvent(new Event(ADS_CONSENT_EVENT));
}

window.addEventListener('storage', (event: StorageEvent) => {
  if (event.key !== CONSENT_KEY && event.key !== null) return;
  consentInMemoryOnly = false;
  if (getAdsConsent() !== 'granted') withdrawAdsConsent();
  window.dispatchEvent(new Event(ADS_CONSENT_EVENT));
});

export function withoutGoogleClickIdentifiers(href: string): string {
  const url = new URL(href);
  for (const name of clickNames) url.searchParams.delete(name);
  return url.href;
}

/** Store only after consent. Keep click IDs on first-party routes across sign-in/Stripe returns. */
export function attributedLocation(href: string): string {
  if (getAdsConsent() !== 'granted') return href;
  const fromUrl = readClickParameters(href);
  if (Object.keys(fromUrl).length) pendingClickParameters = fromUrl;
  try {
    if (Object.keys(pendingClickParameters).length) {
      sessionStorage.setItem(CLICK_KEY, JSON.stringify({ parameters: pendingClickParameters, timestamp: Date.now() }));
    } else {
      const stored = JSON.parse(sessionStorage.getItem(CLICK_KEY) || 'null');
      if (stored && Date.now() - stored.timestamp < 30 * 24 * 60 * 60 * 1000) {
        const candidate = new URL(window.location.origin);
        for (const name of clickNames) if (typeof stored.parameters?.[name] === 'string') candidate.searchParams.set(name, stored.parameters[name]);
        pendingClickParameters = readClickParameters(candidate.href);
      }
    }
  } catch { /* Google cookies still provide attribution when storage is blocked. */ }
  const url = new URL(href);
  for (const [name, value] of Object.entries(pendingClickParameters)) if (!url.searchParams.has(name)) url.searchParams.set(name, value);
  return url.href;
}

/** Never send Stripe client secrets, account parameters or session IDs as a page URL. */
function measurementLocation(): string {
  const url = new URL(window.location.href);
  const clean = new URL(url.pathname, url.origin);
  for (const name of [...clickNames, 'utm_source', 'utm_medium', 'utm_campaign']) {
    const value = url.searchParams.get(name);
    if (value) clean.searchParams.set(name, value);
  }
  return clean.href;
}

export function loadGoogleAdsTag(): Promise<boolean> {
  if (!config || getAdsConsent() !== 'granted') return Promise.resolve(false);
  if (tagReady) {
    googleWindow.gtag?.('consent', 'update', { ...consentDenied, ad_storage: 'granted', ad_user_data: 'granted' });
    return Promise.resolve(true);
  }
  if (tagPromise) return tagPromise;
  tagPromise = new Promise<boolean>((resolve) => {
    googleWindow.dataLayer = googleWindow.dataLayer || [];
    // Match Google's gtag queue format (Arguments objects, not custom events).
    // eslint-disable-next-line prefer-rest-params
    googleWindow.gtag = googleWindow.gtag || function () { googleWindow.dataLayer!.push(arguments); };
    googleWindow.gtag('consent', 'default', consentDenied);
    googleWindow.gtag('consent', 'update', { ...consentDenied, ad_storage: 'granted', ad_user_data: 'granted' });
    googleWindow.gtag('set', 'ads_data_redaction', true);
    googleWindow.gtag('set', 'allow_ad_personalization_signals', false);
    googleWindow.gtag('js', new Date());
    googleWindow.gtag('config', config.id, {
      send_page_view: false,
      allow_enhanced_conversions: false,
      allow_ad_personalization_signals: false,
      page_location: measurementLocation(),
      page_referrer: document.referrer ? new URL(document.referrer).origin : '',
    });
    const script = document.createElement('script');
    script.id = 'jampackd-google-ads-tag';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.id)}`;
    const timeout = window.setTimeout(() => finish(false), 8000);
    const finish = (loaded: boolean) => {
      window.clearTimeout(timeout);
      script.onload = null;
      script.onerror = null;
      tagReady = loaded;
      if (!loaded) script.remove();
      resolve(loaded);
    };
    script.onload = () => finish(true);
    script.onerror = () => finish(false);
    document.head.appendChild(script);
  });
  // One script attempt per page. A blocker/failure never affects checkout.
  return tagPromise;
}

const tracker = config ? createPurchaseTracker({
  config,
  consent: getAdsConsent,
  loadTag: loadGoogleAdsTag,
  send: parameters => googleWindow.gtag?.('event', 'conversion', { ...parameters, page_location: measurementLocation() }),
  storage: {
    getItem: key => sessionStorage.getItem(key),
    setItem: (key, value) => sessionStorage.setItem(key, value),
  },
}) : null;

export function trackVerifiedPurchase(receipt: PurchaseReceipt): Promise<boolean> {
  return tracker ? tracker(receipt) : Promise.resolve(false);
}

export function openAdsPreferences(): void {
  window.dispatchEvent(new Event(ADS_PREFERENCES_EVENT));
}
